import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Space, Spin, Tag, Typography } from 'antd';
import { ReloadOutlined, InboxOutlined, CheckCircleOutlined, ClockCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchOrderWarehouseSummary } from '../api/orderWarehouseApi';
import type { OrderCostPlan, OrderCostPlanLine, OrderWarehouseLine, OrderWarehouseSummary } from '../../../types/orderWarehouseSummary';
import { formatUnit } from '../../../utils/units';
import { formatCurrency, formatCurrencyUnitPrice } from '../../../utils/currency';
import { formatDateEuropean } from '../../../utils/dateUtils';
import { lineCoverage, orderFulfillment, type LineCoverage, type OrderFulfillment } from './orderWarehouseStatus';
import type { OrderProduct } from '../../../types/order';
import { getOrderProductDisplayDimensions } from '../orderProductDisplay';
import './OrderWarehouseSection.css';
import { formatLocalizedExchangeRate, formatLocalizedQuantity } from '../../../utils/numberFormatting';

interface Props {
  orderId: string;
  canViewMargin: boolean;
  refreshKey?: unknown;
  onRefreshOrder?: () => Promise<unknown>;
  /**
   * Label of the plan result from the ZK card's one margin rule
   * (process/orderMarginStage.ts) so „Szczegóły” never says „Planowana marża”
   * next to a header „Marża końcowa”. Default: „Planowana marża”.
   */
  resultLabel?: string;
  /** „Wydaj” is pointless on an order closed by invoice or fully issued. */
  canIssue?: boolean;
  /** Order lines, for the dimensions under the product names (joined by line id). */
  products?: OrderProduct[];
}

const formatQuantity = (value: number, unit: string | null) =>
  `${formatLocalizedQuantity(value, 'pl', 4)} ${formatUnit(unit, 'pl')}`;

/** One order line of the cost plan: issued, allocated batches, average and unknown parts. */
function PlanLine({ line, name, showAmounts }: { line: OrderCostPlanLine; name: string | null; showAmounts: boolean }) {
  const { t } = useTranslation(['orders']);
  const amount = (value: number) => showAmounts ? formatCurrency(value, 'PLN') : null;
  const empty = line.issuedCostPLN === 0 && line.fifo.length === 0 && line.averageCostPLN == null && !line.costPending;
  return <div className="order-warehouse-plan-line">
    {name && <strong className="order-warehouse-plan-name">{name}</strong>}
    <ul className="order-warehouse-plan-parts">
      {line.issuedCostPLN > 0 && <li>
        <span>{t('orders:warehouseFlow.lineIssued')}</span><span className="order-warehouse-plan-amount">{amount(line.issuedCostPLN)}</span>
      </li>}
      {line.fifo.map((part, index) => <li key={`${part.batchNumber}-${index}`}>
        <span>
          {t('orders:warehouseFlow.lineAllocated', { quantity: formatQuantity(part.quantity, part.unit) })}
          {' · '}{t('orders:warehouseFlow.batchLabel', { pz: part.pzNumber ?? '—', batch: part.batchNumber ?? '—' })}
          {' '}<Tag>{t(`orders:warehouseFlow.source.${part.source}`)}</Tag>
        </span>
        <span className="order-warehouse-plan-amount">{amount(part.costPLN)}</span>
      </li>)}
      {line.averageCostPLN != null && line.averageQuantity != null && <li>
        <span>
          {t('orders:warehouseFlow.lineAverage', { quantity: formatQuantity(line.averageQuantity, line.averageUnit) })}
          {line.averagePurchasePricePLN != null && <span className="order-warehouse-average-price">
            {' '}{formatCurrencyUnitPrice(line.averagePurchasePrice ?? line.averagePurchasePricePLN, line.averagePurchaseCurrency || 'PLN', 'pl')}
            {line.averagePurchasePriceUnit && ` / ${formatUnit(line.averagePurchasePriceUnit, 'pl')}`}
            {line.averageExchangeRate != null && ` ${t('orders:warehouseFlow.averageRate', {
              rate: formatLocalizedExchangeRate(line.averageExchangeRate, 'pl'),
              date: line.averageExchangeRateDate ? formatDateEuropean(line.averageExchangeRateDate) : t('orders:warehouseFlow.averageRateOrder'),
            })}`}
          </span>}
          {showAmounts && <>{' '}<Tag color="gold">{t('orders:warehouseFlow.estimated')}</Tag></>}
        </span>
        <span className="order-warehouse-plan-amount">{amount(line.averageCostPLN)}</span>
      </li>}
      {line.costPending && <li className="order-warehouse-plan-unknown">
        <span>
          {line.pendingQuantity != null
            ? t('orders:warehouseFlow.lineUnknown', { quantity: formatQuantity(line.pendingQuantity, line.unit) })
            : t('orders:warehouseFlow.lineUnknownNoQuantity')}
          {line.pendingReason && ` — ${t(`orders:warehouseFlow.pendingReason.${line.pendingReason}`)}`}
        </span>
        {showAmounts && <span className="order-warehouse-plan-amount">—</span>}
      </li>}
      {empty && <li><span>{t('orders:warehouseFlow.lineNothingOpen')}</span>{showAmounts && <span className="order-warehouse-plan-amount">{amount(0)}</span>}</li>}
      {line.warnings.map(warning => <li key={warning} className="order-warehouse-plan-unknown">
        <span>{t(`orders:warehouseFlow.lineWarning.${warning}`)}</span>
      </li>)}
    </ul>
  </div>;
}

const planPartCount = (plan: OrderCostPlan) => plan.lines.reduce((sum, line) => sum
  + (line.issuedCostPLN > 0 ? 1 : 0) + line.fifo.length + (line.averageCostPLN != null ? 1 : 0) + (line.costPending ? 1 : 0), 0);

const STATUS_ICON: Record<OrderFulfillment, React.ReactNode> = {
  ISSUED: <CheckCircleOutlined />, AVAILABLE: <CheckCircleOutlined />, SHORTAGE: <ClockCircleOutlined />, UNKNOWN: <QuestionCircleOutlined />,
};
const STATUS_TONE: Record<OrderFulfillment, string> = { ISSUED: 'success', AVAILABLE: 'success', SHORTAGE: 'warning', UNKNOWN: 'neutral' };

/** One sentence answering „what is happening with this order's goods"; quantities only, no finance. */
function FulfillmentBanner({ lines, canViewMargin }: { lines: OrderWarehouseLine[]; canViewMargin: boolean }) {
  const { t } = useTranslation(['orders']);
  if (lines.length === 0) return null;
  const coverages = lines.map(lineCoverage);
  const state = orderFulfillment(coverages);
  const quantity = (value: number, line: OrderWarehouseLine) => formatQuantity(value, line.unit);
  let title: string;
  let help: string;
  if (state === 'ISSUED') {
    title = t('orders:warehouseFlow.statusIssued');
    help = t('orders:warehouseFlow.statusIssuedHelp');
  } else if (state === 'AVAILABLE') {
    title = t('orders:warehouseFlow.statusAvailable');
    help = t('orders:warehouseFlow.statusAvailableHelp');
  } else if (state === 'UNKNOWN') {
    title = t('orders:warehouseFlow.statusUnknown');
    help = t('orders:warehouseFlow.statusUnknownHelp');
  } else {
    const short = coverages.filter(line => line.status === 'WAITING' || line.status === 'PARTIAL');
    if (lines.length === 1) {
      const [line] = lines;
      const [cover] = coverages;
      title = t('orders:warehouseFlow.statusStock', {
        available: formatLocalizedQuantity(cover.available, 'pl', 4),
        total: quantity(cover.available + cover.missing, line),
      });
    } else {
      title = t('orders:warehouseFlow.statusShortageMany', { count: short.length, total: lines.length });
    }
    help = t(canViewMargin ? 'orders:warehouseFlow.statusShortageCostHelp' : 'orders:warehouseFlow.statusShortageHelp');
  }
  return <div className={`order-warehouse-status order-warehouse-status--${STATUS_TONE[state]}`} role="status">
    <span className="order-warehouse-status-icon" aria-hidden="true">{STATUS_ICON[state]}</span>
    <div>
      <div className="order-warehouse-status-title">{title}</div>
      <div className="order-warehouse-status-help">{help}</div>
    </div>
  </div>;
}

/** Ordered quantity split into issued / in stock / still to be delivered. */
function CoverageLine({ line, cover, product }: { line: OrderWarehouseLine; cover: LineCoverage; product?: OrderProduct }) {
  const { t } = useTranslation(['orders']);
  const qty = (value: number) => formatQuantity(value, line.unit);
  const share = (value: number) => cover.ordered > 0 ? `${Math.min(100, Math.max(0, value / cover.ordered * 100))}%` : '0%';
  const legend: { key: string; tone: string; text: string }[] = [];
  if (cover.status === 'UNKNOWN') legend.push({ key: 'unknown', tone: 'unknown', text: t('orders:warehouseFlow.unknownAvailability') });
  if (cover.issued > 0) legend.push({ key: 'issued', tone: 'issued', text: t('orders:warehouseFlow.legendIssued', { quantity: qty(cover.issued) }) });
  if (cover.available > 0) legend.push({ key: 'available', tone: 'available', text: t('orders:warehouseFlow.legendAvailable', { quantity: qty(cover.available) }) });
  if (cover.missing > 0) legend.push({ key: 'missing', tone: 'missing', text: t('orders:warehouseFlow.legendMissing', { quantity: qty(cover.missing) }) });
  const dimensions = getOrderProductDisplayDimensions(product);
  return <div className="order-warehouse-line">
    <div className="order-warehouse-line-head">
      <span className="order-warehouse-line-name">
        <strong>{line.name}</strong>
        {dimensions && <small data-testid="order-warehouse-line-dimensions">{dimensions}</small>}
      </span>
      <span className="order-warehouse-quantity">{t('orders:warehouseFlow.legendOrdered', { quantity: qty(cover.ordered) })}</span>
    </div>
    <div className="order-warehouse-bar" aria-hidden="true">
      {cover.status === 'UNKNOWN' ? <span className="order-warehouse-bar-unknown" style={{ width: '100%' }} /> : <>
        <span className="order-warehouse-bar-issued" style={{ width: share(cover.issued) }} />
        <span className="order-warehouse-bar-available" style={{ width: share(cover.available) }} />
        <span className="order-warehouse-bar-missing" style={{ width: share(cover.missing) }} />
      </>}
    </div>
    <ul className="order-warehouse-legend">
      {legend.map(item => <li key={item.key}><i className={`order-warehouse-dot order-warehouse-dot--${item.tone}`} aria-hidden="true" />{item.text}</li>)}
      {cover.invoiced > 0 && <li>{t('orders:warehouseFlow.legendInvoiced', { quantity: qty(cover.invoiced) })}</li>}
    </ul>
  </div>;
}

/** Margin as a statement: revenue − goods − outgoing transport − other costs = planned margin. */
function CostPlanSection({ plan, summary, resultLabel }: { plan: OrderCostPlan; summary: OrderWarehouseSummary; resultLabel?: string }) {
  const { t } = useTranslation(['orders']);
  const finance = summary.finance!;
  const amount = (value: number | null | undefined) => value == null ? '—' : formatCurrency(value, 'PLN');
  const lineName = (id: string) => summary.lines.find(product => product.id === id)?.name ?? '—';
  const goodsCost = plan.issuedCostPLN + plan.stockCostPLN + plan.averageCostPLN;
  const showAmounts = planPartCount(plan) > 1;
  const singleLine = plan.lines.length === 1;
  const negative = plan.marginPLN != null && plan.marginPLN < 0;
  // Margin on the goods alone (equals the per-line margin of the Sprzedaż table): shown
  // only when the later costs change it, so a negative result is not read as goods cost.
  const laterCosts = finance.transportCostPLN + finance.otherCostPLN;
  const goodsMarginPLN = plan.revenuePLN == null || plan.costPending || laterCosts <= 0
    ? null : Math.round((plan.revenuePLN - goodsCost) * 100) / 100;
  const goodsMarginPercent = goodsMarginPLN != null && plan.revenuePLN! > 0
    ? Math.round(goodsMarginPLN / plan.revenuePLN! * 10000) / 100 : null;
  return <>
    {plan.closedByInvoice && <Typography.Paragraph type="secondary">{t('orders:warehouseFlow.closedByInvoice')}</Typography.Paragraph>}
    {!plan.closedByInvoice && !plan.participates && <Typography.Paragraph type="secondary">{t(plan.queuePreview ? 'orders:warehouseFlow.draftQueuePreview' : 'orders:warehouseFlow.draftNoAllocation')}</Typography.Paragraph>}
    <div className="order-warehouse-ledger">
      <div className="order-warehouse-ledger-row order-warehouse-ledger-row--main">
        <span>{t('orders:warehouseFlow.revenue')}</span>
        <span className="order-warehouse-plan-amount">{amount(plan.revenuePLN)}</span>
      </div>
      <div className="order-warehouse-ledger-group">
        <div className="order-warehouse-ledger-row">
          <div>
            <span><span className="order-warehouse-ledger-sign" aria-hidden="true">−</span>{t('orders:warehouseFlow.goodsCost')}</span>
            {plan.costPending && <div className="order-warehouse-ledger-note">{t('orders:warehouseFlow.goodsCostPartial')}</div>}
            <div className="order-warehouse-plan-lines">
              {plan.lines.map(line => <PlanLine key={line.id} line={line} name={singleLine ? null : lineName(line.id)} showAmounts={showAmounts} />)}
            </div>
          </div>
          <span className="order-warehouse-plan-amount">{amount(goodsCost)}</span>
        </div>
      </div>
      {goodsMarginPLN != null && <div className="order-warehouse-ledger-row order-warehouse-ledger-row--main order-warehouse-ledger-subtotal">
        <div>
          <span><span className="order-warehouse-ledger-sign" aria-hidden="true">=</span>{t('orders:warehouseFlow.goodsMargin')}</span>
          <div className="order-warehouse-ledger-note">{t('orders:warehouseFlow.goodsMarginHelp')}</div>
        </div>
        <span className="order-warehouse-plan-amount">{amount(goodsMarginPLN)}
          {goodsMarginPercent != null && <small> ({formatLocalizedQuantity(goodsMarginPercent, 'pl')}%)</small>}</span>
      </div>}
      <div className="order-warehouse-ledger-group">
        <div className="order-warehouse-ledger-row">
          <div>
            <span><span className="order-warehouse-ledger-sign" aria-hidden="true">−</span>{t('orders:warehouseFlow.transportCost')}</span>
            <div className="order-warehouse-ledger-note">{t('orders:warehouseFlow.transportCostHelp')}</div>
          </div>
          <span className="order-warehouse-plan-amount">{amount(finance.transportCostPLN)}</span>
        </div>
        <div className="order-warehouse-ledger-row">
          <span><span className="order-warehouse-ledger-sign" aria-hidden="true">−</span>{t('orders:warehouseFlow.otherCost')}</span>
          <span className="order-warehouse-plan-amount">{amount(finance.otherCostPLN)}</span>
        </div>
      </div>
    </div>
    {finance.costProvisional && <Alert type="warning" showIcon message={t('orders:warehouseFlow.costProvisional')} />}
    {plan.costPending && <Alert type="warning" showIcon message={t('orders:warehouseFlow.averageMissing')} />}
    {plan.totalCostPLN != null && <div className={`order-warehouse-result${negative ? ' order-warehouse-result--negative' : ''}`}>
      <div>
        <span>{resultLabel ?? t('orders:warehouseFlow.plannedMargin')}</span>
        <span>{t('orders:warehouseFlow.plannedCost')}: {amount(plan.totalCostPLN)}</span>
      </div>
      <strong>{amount(plan.marginPLN)}
        {plan.marginPercent != null && <small> ({formatLocalizedQuantity(plan.marginPercent, 'pl')}%)</small>}</strong>
    </div>}
    <details className="order-warehouse-how">
      <summary>{t('orders:warehouseFlow.howWeCount')}</summary>
      <p>{t('orders:warehouseFlow.allocationHelp')}</p>
    </details>
  </>;
}

export default function OrderWarehouseSection({ orderId, canViewMargin, refreshKey, onRefreshOrder, resultLabel, canIssue = true, products = [] }: Props) {
  const { t } = useTranslation(['orders', 'common', 'wz']);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<{ orderId: string; summary?: OrderWarehouseSummary; failed?: boolean }>();
  useEffect(() => {
    let active = true;
    setState(undefined);
    fetchOrderWarehouseSummary(orderId).then(
      summary => { if (active) setState({ orderId, summary }); },
      () => { if (active) setState({ orderId, failed: true }); },
    );
    return () => { active = false; };
  }, [orderId, revision, refreshKey]);
  // A return from the PZ/catalog screen must refresh the live valuation.
  useEffect(() => {
    const refresh = () => {
      if (!document.hidden) {
        if (onRefreshOrder) void onRefreshOrder().catch(() => setRevision(value => value + 1));
        else setRevision(value => value + 1);
      }
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [onRefreshOrder]);
  const summary = state?.orderId === orderId ? state.summary : undefined;
  const failed = state?.orderId === orderId && state.failed;
  const plan = canViewMargin ? summary?.finance?.plan : undefined;
  const noStock = !!summary?.lines.some(line => (line.remainingQuantity ?? 0) > 0) && summary.lines.every(line => line.availableQuantity === 0);
  return <Card className="order-warehouse" title={<Space><InboxOutlined />{t('orders:warehouseFlow.fulfillment')}</Space>}
    extra={<Button type="text" icon={<ReloadOutlined />} aria-label={t('orders:warehouseFlow.refresh')}
      onClick={() => setRevision(value => value + 1)} />}>
    {failed ? <Alert type="warning" showIcon message={t('orders:warehouseFlow.loadError')}
      action={<Button onClick={() => setRevision(value => value + 1)}>{t('common:retry')}</Button>} />
      : !summary ? <Spin aria-label={t('common:messages.loading')} /> : <>
        <FulfillmentBanner lines={summary.lines} canViewMargin={canViewMargin} />
        <div className="order-warehouse-lines">
          {summary.lines.map(line => <CoverageLine key={line.id} line={line} cover={lineCoverage(line)} product={products.find(product => product.id === line.id)} />)}
        </div>
        {summary.documents.length > 0 && <Space wrap className="order-warehouse-documents">
          {summary.documents.map(document => <Link key={document.id} to={`/app/wz/${encodeURIComponent(document.id)}`}>
            {document.wzNumber}
          </Link>)}
        </Space>}
        {canViewMargin && summary.finance && <section className="order-warehouse-valuation">
          <div className="order-warehouse-valuation-heading">
            <Typography.Title level={5}>{t('orders:warehouseFlow.planningTitle')}</Typography.Title>
            <Space wrap>
              {plan && !plan.participates && !plan.closedByInvoice && <Tag>{t('orders:warehouseFlow.notConfirmed')}</Tag>}
              {plan?.usesAverage && <Tag color="gold">{t('orders:warehouseFlow.estimated')}</Tag>}
            </Space>
          </div>
          {plan ? <CostPlanSection plan={plan} summary={summary} resultLabel={resultLabel} />
            : summary.finance.costPending && <Alert type="warning" showIcon message={t('orders:warehouseFlow.costPending')} />}
        </section>}
      </>}
    {canIssue && <div className="order-warehouse-actions">
      <Link to={`/app/wz/new?orderId=${encodeURIComponent(orderId)}&returnTo=${encodeURIComponent(`/app/orders/${orderId}`)}`}>
        <Button type={noStock ? 'default' : 'primary'}>{t('orders:warehouseFlow.issueGoods')}</Button>
      </Link>
    </div>}
  </Card>;
}
